<script setup>
import OrderFallback from './OrderFallback.vue'
import { useFutureStore } from '~/store/future'
import xImage from '~/components/common/xImage.vue'
import xTipsSimple from '~/components/common/xTipsSimple.vue'
import xOptInput from '~/components/common/xOptInput.vue'
import { useStore } from '~/store'
import { useOrderStore } from '~/store/order'
import { positionLoading } from '~/apis/socket'
import Loading from '~/components/common/icons/Loading.vue'
import { usePanel } from '~/hooks/panel'
import { onlyAllowNumber } from '@/utils/tools'
import { useBonusStore } from '~/store/bonus'

const { t } = useI18n()
const store = useStore()
const order = useOrderStore()
const future = useFutureStore()
const message = useMessage()
const { placeOrder } = usePanel()
const bonus = useBonusStore()

const priceRaw = ref({})
const amountRaw = ref({})
const otherRaw = ref({})
const adjustMargin = ref({
  show: false,
  posi: {},
})
const tpsl = ref({
  modal: false,
  code: '',
  price: '',
  lots: '',
  defaultValue: null,
  accountType: '',
})

const positions = computed(() => {
  const filters = []
  if (order.params.contractId)
    filters.push({ key: 'contractId', value: +order.params.contractId })
  const arr = filters.reduce((acc, next) => {
    acc = acc.filter(i => i[next.key] === next.value)
    return acc
  }, future.positions)
  const prevPosition = arr.filter(i => i.code === future.code)
  const nextPosition = arr.filter(i => i.code !== future.code).sort((prev, next) => {
    return future.getInfo(next.code).weight - future.getInfo(prev.code).weight
  })
  return prevPosition.concat(nextPosition)
})

const isBtnDisabled = computed(() => {
  return i => (bonus.isUsedGrants || bonus.isButtonDisabled) && i.accountType === 10
})

whenever(() => future.positions, (nv, ov = []) => {
  nv.forEach((i) => {
    const cur = ov.find(j => j.id === i.id)
    if (!cur || cur.posiQty !== i.posiQty) {
      priceRaw.value[i.id] = String(i.lastPrice)
      amountRaw.value[i.id] = String(mul(i.posiQty, i.size).abs())
      otherRaw.value[i.id] = { per: 100 }
    }
  })
  const newBonusList = nv.filter(v => v.accountType === 10)
  const oldBonusList = ov.filter(v => v.accountType === 10)
  if (newBonusList.length !== oldBonusList.length) {
    bonus.getBounsUsedInfo()
  }
  else if (newBonusList.length === 1) {
    newBonusList[0].amount !== oldBonusList[0].amount && bonus.getBounsUsedInfo()
  }
}, {
  immediate: true,
})

const lists = computed(() => {
  return [
    {
      label: t('仓位数量'),
      tips: t('现在持有的数量，包含平仓尚未成交的部分'),
      key: 'posiQty',
    },
    {
      label: t('开仓均价'),
      tips: t('目前仓位，开仓平均成本价格'),
      key: 'openPrice',
    },
    {
      label: t('标记价'),
      tips: t('标记价格由即时指数价格和即将到来的资金费率决定，反映该合约目前的合理价格。标记价格用于计算仓位浮动盈亏和强平触发。'),
      formatter(_, { id }) {
        return future.positionsExtra[id]?.mp
      },
      key: 'markPrice',
    },
    {
      label: t('保证金'),
      tips: t('保证金=持仓仓位价值/杠杆'),
      key: 'initMargin',
      unit: 'USDT',
      formatter(v) {
        return fix(v, 2)
      },
    },
    {
      label: `${t('未实现盈亏')}(ROE)`,
      tips: t('未实现盈亏计算方式注意：仅用于展示。并不影响保证金计算的方式，真实盈亏以实际成交为准'),
      classFn(_, { id }) {
        const sign = +future.positionsExtra[id]?.upnl
        return { 'text-buy': sign > 0, 'text-sell': sign < 0 }
      },
      formatter(_, { id, initMargin }) {
        return `${fix(future.positionsExtra[id]?.upnl, 2)} USDT(${percent(div(future.positionsExtra[id]?.upnl || 0, initMargin), 2)})`
      },
      key: 'unRealizedProfit',
    },
    {
      label: t('档位维持保证金率'),
      tips: t('持仓档位对应的数值，当仓位的保证金率小于维持保证金率时，将会触发强制减仓或爆仓'),
      formatter(v) {
        return percent(v, 2, false)
      },
      key: 'maintainMarginRate',
    },
    // {
    //   label: t('盈亏平衡价'),
    //   tips: t('盈亏平衡价为此仓位平仓时总收益为0的价格，盈亏平衡价=（开仓以来的总手续费用-资金费用-已平仓收益+开仓方向×开仓均价×持仓数量）÷[持仓数量×（开仓方向-吃单费率）]，其中开仓方向，多头为1，空头为-1。'),
    //   key: 'breakevenPrive',
    // },
    {
      label: t('仓位止盈止损'),
      tips: t('仓位止盈止损针对整个仓位（已有仓位及后续的加仓或者减仓）。当市场最新成交价格/合理标记价格达到触发价格时，将以最优成交价将您此仓位设置的数量进行委托挂单。该仓位全部平仓后，该止盈止损将自动消失。'),
      value: 1,
      key: 'tpsl',
    },
    // {
    //   label: t('回报率'),
    //   tips: t('回报率=未实现盈亏÷初始保证金，初始保证金=开仓均价x持仓数量÷杠杆÷保证金币种指数价格'),
    //   classFn(_, { id }) {
    //     const sign = +future.positionsExtra[id]?.upnl
    //     return { 'text-buy': sign > 0, 'text-sell': sign < 0 }
    //   },
    //   formatter(_, { id, initMargin }) {
    //     return percent(div(future.positionsExtra[id]?.upnl || 0, initMargin), 2)
    //   },
    //   key: 'roe',
    // },
    {
      label: t('风险率'),
      tips: t('风险率为仓位维持保证金与仓位权益的比例，风险率越高爆仓风险越大。'),
      classFn(_, { id }) {
        const p = future.getRiskRateById(id)
        return { 'text-buy': p < 50, 'text-warning': p >= 50 && p <= 70, 'text-sell': p > 70 }
      },
      formatter(_, { id }) {
        return integerPercent(future.getRiskRateById(id), 2)
      },
      key: 'riskRate',
    },
    {
      label: t('预估强平价'),
      tips: t('基于目前资金，当MMR达到100%时的估算价格。预估强平价可能会发生变化，实际强平/减仓触发以MMR大于100%为准。'),
      formatter(_, { id, code }) {
        return +future.positionsExtra[id]?.flp ? fix(future.positionsExtra[id]?.flp, future.getInfo(code)?.ps) : '--'
      },
      key: 'liquidationPrice',
    },
  ]
})
function handleAdjustMargin(i) {
  adjustMargin.value.posi = i
  adjustMargin.value.show = true
}
function handleTpsl(i) {
  const [tp = {}, sl = {}] = future.getStops(i)
  const price = `${i.openPrice}`
  tpsl.value.lots = `${Math.abs(i.posiQty)}`
  tpsl.value.side = i.side
  tpsl.value.code = i.code
  tpsl.value.price = price
  tpsl.value.accountType = i.accountType

  tpsl.value.defaultValue = {
    tp: { triggerPrice: tp.stopPrice ?? '', orderSubType: tp.orderSubType, price: +tp.price ? tp.price : '' },
    sl: { triggerPrice: sl.stopPrice ?? '', orderSubType: sl.orderSubType, price: +sl.price ? sl.price : '' },
  }
  tpsl.value.modal = true
}
async function closePosition({ id, side, posiQty, code, accountType, size }, flash, flashAll) {
  const price = priceRaw.value[id]
  const quantity = Math.min(div(amountRaw.value[id], size).toFixed(0), Math.abs(posiQty))
  const { base } = future.getInfo(code)
  if (!+quantity) {
    return message.error(t('最小下单数量{0}', [`${size} ${base}`]))
  }
  if (!+price && !flash) {
    return message.error(t('请输入价格'))
  }
  const { success } = placeOrder({
    price: flash ? '' : price,
    quantity: flashAll ? Math.abs(posiQty) : quantity,
    reduceOnly: true,
    side: -side,
    code,
    accountType,
    orderConfirmType: flashAll ? '' : flash ? 'MARKET' : 'LIMIT',
    ignoreLoading: flashAll,
  }).then(() => {
    if (accountType === 10 && bonus.isUsedGrants) {
      bonus.getaGrantsList()
      setTimeout(() => {
        bonus.getBounsUsedInfo()
      }, 1000)
    }
  })
  return success
}
function closePositionAll() {
  const promises = future.positions.map(i => closePosition(i, true, true))
  Promise.all(promises)
  future.modal.flashAll = false
}

function onPriceStep(side, id, code) {
  const { ps } = future.getInfo(code)
  const step = div(1, 10 ** ps)
  const price = +priceRaw.value[id]
  const fn = side === 1 ? add : sub
  priceRaw.value[id] = `${fn(price, step)}`
}

function onAmountStep(side, { id, posiQty, size }) {
  const base = `${Math.abs(mul(posiQty, size))}`
  const fn = side === 1 ? add : sub
  const v = fn(+amountRaw.value[id], size)
  amountRaw.value[id] = v > +base ? base : v < size ? size : `${v}`
}

function onPercentAmount(per, i) {
  const lots = mul(Math.abs(i.posiQty), per).div(100).toFixed(0)
  const { as } = future.getInfo(i.code)
  amountRaw.value[i.id] = mul(lots, i.size).toFixed(as)
  otherRaw.value[i.id].per = per
}

function allowInput(value, i) {
  const { as } = future.getInfo(i.code)
  return onlyAllowNumber(value, as)
}

const TpSlModal = defineAsyncComponent(() => import('@/components/futures/modal/tpsl/index.vue'))
const AdjustMarginModal = defineAsyncComponent(() => import('@/components/futures/modal/AdjustMargin.vue')) // 组仓调整保证金弹框

function confirm(info) {
  const promises = []
  const arr = ['tp', 'sl']

  arr.forEach((k) => {
    const i = info?.[k]
    if (i.triggerPrice) {
      promises.push(placeOrder({
        code: tpsl.value.code,
        price: i.priceType === 'MARKET' ? '' : i.price,
        triggerDirection: +i.triggerPrice > +tpsl.value.price ? 1 : -1,
        stopPrice: i.triggerPrice,
        stopType: 1,
        quantity: tpsl.value.lots,
        side: -tpsl.value.side,
        accountType: tpsl.value.accountType,
        orderSubType: i.orderSubType,
        reduceOnly: true,
        ignoreLoading: true,
      }))
    }
  })
  Promise.all(promises).then(() => {
    info.close?.()
  })
}

function reverse(i) {
  future.modal.reverse = true
  future.modalData.reverse = i
}
</script>

<template>
  <div class="space-y-2">
    <div v-for="i in positions" :key="i.id" class="border bg-pageTertiary rounded-xl border-border">
      <div class="flex items-center px-4 pt-3 space-x-2">
        <xImage :src="store.getSpotIconByCode(future.getInfo(i.code).base)" class="w-6 h-6" />
        <OrdersFuturesPositionInfo is-link :data="i" :class="{ 'text-buy': i.side === 1, 'text-sell': i.side === -1 }" />
        <em class="flex items-center justify-center w-6 h-6 border rounded-full cursor-pointer hover:bg-primary hover:text-textOnBtn hover:border-primary border-border bg-fillPagePrimary" @click="future.modal.adjustLeverage = true;future.modalData.adjustLeverage = { code: i.code, accountName: i.accountName }">
          <SvgoFuturesIconTpslEdit class="w-3 h-3" />
        </em>
        <!-- <em class="flex items-center hover:bg-primary hover:border-primary cursor-pointer justify-center w-6 h-6 border rounded-full border-[#575757] bg-bg2">
          <Share />
        </em> -->
      </div>
      <div class="p-4 mt-3 bg-fillPagePrimary rounded-xl">
        <div class="grid grid-cols-5 text-xs">
          <dl v-for="v in lists" :key="v.key" class="flex flex-col pb-3 [&>dd]:font-medium space-y-1">
            <dt class="text-textSecondary">
              <xTipsSimple :text="v.tips">
                <span :class="{ 'cursor-pointer border-b border-border border-dashed': v.tips }">{{ v.label }}</span>
              </xTipsSimple>
            </dt>
            <dd v-if="v.key === 'initMargin'" class="flex items-center space-x-2 rtl:space-x-reverse">
              <span>{{ v.formatter?.(i.initMargin, i) }}</span>
              <SvgoFuturesIconTpslEdit v-if="i.isolated && i.isFuture" class="w-3 h-3 cursor-pointer hover:text-primary" @click="handleAdjustMargin(i)" />
            </dd>
            <dd v-else-if="v.key === 'tpsl'" class="flex items-center space-x-2 rtl:space-x-reverse">
              <span><em class="text-buy">{{ fix(future.getStops(i)[0]?.stopPrice ?? '--', future.getInfo(i.code)?.ps) }}</em>/<em class="text-sell">{{ fix(future.getStops(i)[1]?.stopPrice ?? '--', future.getInfo(i.code)?.ps) }}</em></span>
              <SvgoFuturesIconTpslEdit class="w-3 h-3 cursor-pointer hover:text-primary" @click="handleTpsl(i)" />
            </dd>
            <dd v-else-if="v.key === 'posiQty'">
              <span>{{ `${mul(i[v.key], future.getInfo(i.code).size)} ${future.getInfo(i.code).base}` }}</span>
              <span class="text-textSecondary">{{ `≈${fix(future.getRealAmount(i[v.key], { code: i.code, forceQuote: true }), future.getInfo(i.code).ps)}${future.getInfo(i.code).baseM}` }}</span>
            </dd>
            <dd v-else :class="v.classFn?.(i[v.key], i)">
              {{ v.formatter?.(i[v.key], i) ?? i[v.key] ?? '--' }} {{ v.unit }}
            </dd>
          </dl>
        </div>
        <div class="flex pt-3 border-t border-border">
          <div class="w-[278px]">
            <xOptInput v-model:value="priceRaw[i.id]" size="tiny" :disabled="isBtnDisabled(i)" :allow-input="(value) => onlyAllowNumber(value, future.getInfo(i.code)?.ps)" style="--n-font-size:12px;" @handle-opt="side => onPriceStep(side, i.id, i.code)">
              <template #prefix>
                {{ $t('平仓价格') }}
              </template>
            </xOptInput>
          </div>
          <div class="w-[278px] ms-2.5">
            <n-popover :trigger="isBtnDisabled(i) ? '' : 'hover'" raw :show-arrow="false" placement="bottom-start">
              <template #trigger>
                <xOptInput v-model:value="amountRaw[i.id]" size="tiny" :disabled="isBtnDisabled(i)" style="--n-font-size:12px;" :allow-input="(value) => allowInput(value, i)" @handle-opt="side => onAmountStep(side, i)">
                  <template #prefix>
                    {{ $t('平仓数量') }}
                  </template>
                </xOptInput>
              </template>
              <div class="bg-fillPagePrimary p-2 rounded-md w-[278px] flex justify-between">
                <button v-for="m in [10, 25, 50, 75, 100]" :key="m" class="h-6 rounded-full border border-border px-2.5 text-xs font-medium" :class="{ '!border-textPrimary': otherRaw[i.id].per === m }" @click="onPercentAmount(m, i)">
                  {{ m }}%
                </button>
              </div>
            </n-popover>
          </div>
          <div class="flex items-center ms-6 space-x-3">
            <CommonXButton :disabled="isBtnDisabled(i)" size="small" class="min-w-[80px] !text-xs" @click="closePosition(i)">
              {{ $t('平仓') }}
            </CommonXButton>
            <!-- <CommonXButton class="min-w-[80px] !text-xs" size="small" @click="closePosition(i, true)">
              {{ $t('闪电平仓') }}
            </CommonXButton> -->
            <CommonXButton size="small" type="tertiary" class="min-w-[80px] !text-xs" @click="reverse(i)">
              {{ $t('一键反向') }}
            </CommonXButton>
            <div class="w-px h-8 bg-border" />
            <CommonXButton type="tertiary" size="small" :disabled="isBtnDisabled(i)" class="min-w-[80px] !text-xs" @click="handleTpsl(i)">
              {{ $t('止盈止损') }}
            </CommonXButton>
          </div>
        </div>
      </div>
    </div>
    <div v-if="positionLoading" class="flex justify-center w-full pt-20">
      <Loading class="w-10 h-10" />
    </div>
    <template v-else-if="!positions.length">
      <OrderFallback v-if="future.getMarginBalanceByCode(future.curInfo?.baseM) < 0.01" />
      <CommonNoData v-else />
    </template>
    <OrdersModalPositionReverse />
    <TpSlModal v-bind="tpsl" v-model:show="tpsl.modal" is-pos @confirm="confirm" />
    <AdjustMarginModal v-bind="adjustMargin" v-model:show="adjustMargin.show" />
    <CommonXModal v-model:show="future.modal.flashAll" :title="$t('一键全平')" :ok="closePositionAll" @closed="future.modal.flashAll = false">
      {{ $t('将以最优价平掉全部可用仓位，不含已委托的平仓单') }}
    </CommonXModal>
  </div>
</template>
